import * as React from "react";
import {
    Button,
    makeStyles,
    Persona,
    Popover,
    PopoverSurface,
    PopoverTrigger,
    shorthands,
    tokens,
    Tooltip,
    AppItem,
    Hamburger,
    NavCategory,
    NavCategoryItem,
    NavDrawer,
    NavDrawerBody,
    NavDrawerHeader,
    NavItem,
    NavItemValue,
    NavSectionHeader,
    NavSubItem,
    NavSubItemGroup,
    OnNavItemSelectData
} from "@fluentui/react-components";
import {
    MegaphoneLoud20Filled,
    MegaphoneLoud20Regular,
    Building20Filled,
    Building20Regular,
    bundleIcon,
    PersonCircle32Regular,
    Add20Filled,
    Add20Regular
} from "@fluentui/react-icons";
import { Outlet, useLocation, useMatch, Link } from "react-router-dom";
import { useGlobalState } from "../state/GlobalStateProvider";
import { flexColumn, formStyle, overflowHidden, wrapperContent, wrapperContentWithBackground, drawerCollapsed, drawerContent } from "../styles/GlobalStyles";
import { mobileMaxWidth } from "../util/ResponsiveDesign";

const AnnouncementsIcon = bundleIcon(MegaphoneLoud20Filled, MegaphoneLoud20Regular);
const PublisherIcon = bundleIcon(Building20Filled, Building20Regular);
const AddIcon = bundleIcon(Add20Filled, Add20Regular);

const useStyles = makeStyles({
    drawer: {
        height: "100%",
        borderRadius: "5px"
    },
    overflowHidden: overflowHidden,
    root: {
        ...flexColumn,
        ...shorthands.margin("0px"),
        backgroundColor: tokens.colorNeutralBackground1,
        gap: "0px",
        height: "100%"
    },
    content: wrapperContent,
    contentWithBackground: {
        ...wrapperContentWithBackground,
        padding: "initial"
    },
    drawerExpanded: {
        height: "100%"
    },
    userIcon: {
        marginLeft: "20px",
        cursor: "pointer",
        display: "inline-block"
    },
    form: formStyle,
    seemlessLink: {
        color: "unset",
        textDecoration: "none"
    },
    fullWidth: {
        width: "100%"
    },
    drawerCollapsed,
    drawerContent
});

export type SelectedPage = {
    newSelectedCategory: string | undefined;
    newSelectedValue: string;
};

export const WorkspaceDrawer: React.FC = () => {
    const styles = useStyles();
    const location = useLocation();
    const globalState = useGlobalState();

    const [isOpen, setIsOpen] = React.useState(() => !window.matchMedia(`(max-width: ${mobileMaxWidth}px)`).matches);
 
    const publisherMatch = useMatch("/workspace/publishers/:publisherId/*");

    const selectedValue = React.useMemo(() => {
        const path = location.pathname;
        
        // If we're on a detail page (ends with an ID), select the parent list
        const detailRouteMatch = path.match(/\/(products|customers|licenses)\/[^/]+$/);
        if (detailRouteMatch) {
            // Remove the ID part, keeping just the list route
            return path.substring(0, path.lastIndexOf('/'));
        }
        
        return path;
    }, [location.pathname]);
    
    const selectedCategoryValue = React.useMemo(() => 
        publisherMatch?.params.publisherId || undefined,
        [publisherMatch?.params.publisherId]
    );

    // Manage open/closed state separately from selection
    const [openCategories, setOpenCategories] = React.useState<NavItemValue[]>(() => {
        return publisherMatch?.params.publisherId
            ? [publisherMatch.params.publisherId]
            : [];
    });

    React.useEffect(() => {
        if (publisherMatch?.params.publisherId) {
            setOpenCategories([publisherMatch.params.publisherId]);
        }
    }, [publisherMatch?.params.publisherId]);

    const handleCategoryToggle = (
        ev: Event | React.SyntheticEvent<Element, Event>,
        data: OnNavItemSelectData
    ) => {
        if (openCategories.includes(data.categoryValue as string)) {
            setOpenCategories([]);
        } else {
            setOpenCategories([data.categoryValue as string]);
        }
    };
    
    const renderHamburgerWithToolTip = () => {
        return (
          <Tooltip content="Navigation" relationship="label">
            <Hamburger onClick={() => setIsOpen(!isOpen)} />
          </Tooltip>
        );
      };

    return (
        <div id="drawerRoot" className={styles.content}>
            <div id="drawerWrapper" className={styles.drawerExpanded}>
                <NavDrawer
                    onNavCategoryItemToggle={handleCategoryToggle}
                    selectedValue={selectedValue}
                    selectedCategoryValue={selectedCategoryValue}
                    openCategories={openCategories}
                    open={isOpen}
                    type="inline"
                    multiple={false}
                    className={styles.drawer}
                >
                    <NavDrawerHeader>{renderHamburgerWithToolTip()}</NavDrawerHeader>

                    <NavDrawerBody>
                        <AppItem icon={<PersonCircle32Regular />}>
                            Workspace
                        </AppItem>

                        <Link className={styles.seemlessLink} to="/workspace/announcements">
                            <NavItem icon={<AnnouncementsIcon />} value="/workspace/announcements">
                                Announcements
                            </NavItem>
                        </Link>

                        <NavSectionHeader>
                            Publishers
                        </NavSectionHeader>

                        <Link className={styles.seemlessLink} to="/workspace/publishers/new">
                            <NavItem icon={<AddIcon />} value="/workspace/publishers/new">
                                New
                            </NavItem>
                        </Link>

                        { !globalState.publishers && <NavItem value="loading" disabled>Loading...</NavItem>}

                        { globalState.publishers?.map( o =>
                            <NavCategory key={o.guid} value={o.guid}>
                                <NavCategoryItem className={styles.overflowHidden} title={o.name} icon={<PublisherIcon />}>
                                    {o.name}
                                </NavCategoryItem>
                                <NavSubItemGroup>
                                    <Link className={styles.seemlessLink} to={`/workspace/publishers/${o.guid}/products`}>
                                        <NavSubItem value={`/workspace/publishers/${o.guid}/products`}>
                                            Products
                                        </NavSubItem>
                                    </Link>
                                    <Link className={styles.seemlessLink} to={`/workspace/publishers/${o.guid}/customers`}>
                                        <NavSubItem value={`/workspace/publishers/${o.guid}/customers`}>
                                            Customers
                                        </NavSubItem>
                                    </Link>
                                    <Link className={styles.seemlessLink} to={`/workspace/publishers/${o.guid}/licenses`}>
                                        <NavSubItem value={`/workspace/publishers/${o.guid}/licenses`}>
                                            Licenses
                                        </NavSubItem>
                                    </Link>
                                    <Link className={styles.seemlessLink} to={`/workspace/publishers/${o.guid}`}>
                                        <NavSubItem value={`/workspace/publishers/${o.guid}`}>
                                            Administration
                                        </NavSubItem>
                                    </Link>
                                </NavSubItemGroup>
                            </NavCategory>
                        )}
                    </NavDrawerBody>
                </NavDrawer>
            </div>

            {!isOpen && 
                <div className={styles.drawerCollapsed}>
                    { renderHamburgerWithToolTip() }

                    <Tooltip content="Announcements" relationship="label" positioning="after">
                        <Link className={styles.seemlessLink} to="/workspace/announcements">
                            <NavItem style={{ padding: "5px" }} icon={<AnnouncementsIcon />} value="/workspace/announcements" />
                        </Link>
                    </Tooltip>

                    <Tooltip content="New Publisher" relationship="label" positioning="after">
                        <Link className={styles.seemlessLink} to="/workspace/publishers/new">
                            <NavItem style={{ padding: "5px" }} icon={<AddIcon />} value="/workspace/publishers/new" />
                        </Link>
                    </Tooltip>

                    { globalState.publishers?.map(o => (
                        <Popover key={o.guid} withArrow positioning="after">
                            <PopoverTrigger disableButtonEnhancement>
                                <Persona
                                    className={styles.userIcon}
                                    size="small"
                                    name={o.name}
                                    title={o.name}
                                    avatar={{ color: "neutral" }}
                                    style={{ margin: "0px" }}
                                    primaryText=""
                                />
                            </PopoverTrigger>
                        
                            <PopoverSurface tabIndex={-1}>
                                <div className={styles.form}>
                                    <Link className={styles.seemlessLink} to={`/workspace/publishers/${o.guid}/products`}>
                                        <Button className={styles.fullWidth}>Products</Button>
                                    </Link>
                                    <Link className={styles.seemlessLink} to={`/workspace/publishers/${o.guid}/customers`}>
                                        <Button className={styles.fullWidth}>Customers</Button>
                                    </Link>
                                    <Link className={styles.seemlessLink} to={`/workspace/publishers/${o.guid}/licenses`}>
                                        <Button className={styles.fullWidth}>Licenses</Button>
                                    </Link>
                                    <Link className={styles.seemlessLink} to={`/workspace/publishers/${o.guid}`}>
                                        <Button className={styles.fullWidth}>Administration</Button>
                                    </Link>
                                </div>
                            </PopoverSurface>
                        </Popover>
                    )) }
                </div>
            }
            <div id="drawerContent" className={styles.drawerContent}>
                <div id="drawerOutlet" className={styles.contentWithBackground}>
                    <Outlet />
                </div>
            </div>
        </div>
    );
};